import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Route,
  MapPinned,
  Bell,
  KeyRound,
  Shield,
  Plug,
  Zap,
  AlertTriangle,
  Car,
  Battery,
  Gauge,
  BarChart3,
  Fuel,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import MainNavbar from '../../components/layout/MainNavbar';

const IMAGES = {
  hero: '/2.png',
  realTime: '/rev_device_image.jpeg',
  security: '/rev_info.jpeg',
  insights: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80',
  installation: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&auto=format&fit=crop&q=80',
  whyChoose: 'https://images.unsplash.com/photo-1449965404239-e8e4e2d2cceb?w=800&auto=format&fit=crop&q=80',
};

const DeviceRev = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const realTimeFeatures = [
    { icon: MapPin, label: 'Live GPS tracking with high accuracy' },
    { icon: Route, label: 'Trip history & route playback' },
    { icon: MapPinned, label: 'Geo-fencing alerts' },
    { icon: Bell, label: 'Instant over-speed notifications' },
    { icon: KeyRound, label: 'Ignition ON/OFF alerts' },
  ];

  const securityFeatures = [
    { icon: Plug, label: 'Hidden wired installation (tamper-resistant)' },
    { icon: AlertTriangle, label: 'Power disconnection alerts' },
    { icon: Car, label: 'Engine cut-off support (optional feature)' },
    { icon: Battery, label: 'Backup battery support (model dependent)' },
  ];

  const insightsFeatures = [
    { icon: Gauge, label: 'Speed monitoring' },
    { icon: Zap, label: 'Harsh acceleration & braking alerts' },
    { icon: BarChart3, label: 'Detailed trip & idle reports' },
    { icon: Fuel, label: 'Fuel monitoring support (optional integration)' },
  ];

  const installationFeatures = [
    'Hardwired to vehicle power supply',
    'Hidden mounting for enhanced security',
    'Seamless integration with the TrackYourWheelz portal',
    'Designed for long-term reliability',
  ];

  const whyChoose = [
    'Continuous power supply',
    'Higher security than plug-in devices',
    'Ideal for fleet & commercial use',
    'Real-time alerts & detailed reporting',
    'Durable and dependable performance',
  ];

  return (
    <>
      <style>{`
        .gradient-text-rev {
          background: linear-gradient(135deg, #0ea5e9 0%, #06b6d4 50%, #2563eb 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glass-rev {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .glass-dark-rev {
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .feature-icon-glow-rev {
          box-shadow: 0 0 24px rgba(14, 165, 233, 0.35);
        }
        .img-cover {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }
      `}</style>

      <div
        className={`min-h-screen relative overflow-hidden transition-all duration-500 ${
          theme === 'dark'
            ? 'bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a]'
            : 'bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50'
        }`}
      >
        <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="" />

        {/* Hero with image */}
        <section className="relative z-10 pt-24 pb-12 lg:pt-28 lg:pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate('/home')}
              className={`mb-6 -ml-2 font-medium ${
                theme === 'dark'
                  ? 'text-cyan-300 hover:text-white hover:bg-white/10'
                  : 'text-blue-700 hover:text-blue-900 hover:bg-blue-100'
              }`}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left space-y-5 order-2 lg:order-1">
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    theme === 'dark'
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                      : 'bg-blue-100 text-blue-700 border border-blue-200'
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  Professional GPS Tracker
                </div>
                <h1
                  className={`text-3xl sm:text-4xl md:text-5xl font-black tracking-tight ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  REV
                </h1>
                <p className="text-xl sm:text-2xl font-bold gradient-text-rev">
                  Real-Time Tracking & Control
                </p>
                <p
                  className={`max-w-xl text-base sm:text-lg leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  REV is a state-of-the-art GPS tracker designed for seamless real-time location
                  tracking. Equipped with 4G LTE connectivity, it ensures reliable and swift data
                  transmission, ideal for monitoring vehicles and assets. Geofencing, historical
                  data storage, and customizable alerts keep you informed and in control.
                </p>
                <p
                  className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Whether for personal use or fleet management, REV delivers robust performance
                  and professional installation.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className={`h-12 px-8 text-base font-semibold shadow-lg transition-all duration-300 hover:scale-105 ${
                    theme === 'dark'
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'
                      : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                  }`}
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] max-h-[340px] lg:max-h-none">
                  <img
                    src={IMAGES.hero}
                    alt="REV GPS tracker"
                    className="img-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1603638725135-928baf863eff?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Real-Time Tracking & Control - image left */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Card
              className={`overflow-hidden border-0 shadow-2xl rounded-2xl md:rounded-3xl p-0 ${
                theme === 'dark' ? 'glass-dark-rev' : 'glass-rev'
              }`}
            >
              <div className="flex flex-col lg:flex-row">
                <div className="relative w-full lg:w-2/5 min-h-[240px] lg:min-h-[320px] flex-shrink-0">
                  <img
                    src={IMAGES.realTime}
                    alt="Real-time tracking"
                    className="img-cover w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1603638725135-928baf863eff?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent lg:from-black/40" />
                  <div className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center feature-icon-glow-rev">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                </div>
                <CardContent className="flex-1 p-6 md:p-8 lg:p-10">
                  <h2
                    className={`text-xl md:text-2xl font-bold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Real-Time Tracking & Control
                  </h2>
                  <p
                    className={`text-sm md:text-base mb-6 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Stay in full control with:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                    {realTimeFeatures.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-3 p-3 md:p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
                            theme === 'dark'
                              ? 'bg-white/5 hover:bg-white/10 border border-white/10'
                              : 'bg-cyan-50/80 hover:bg-cyan-100/80 border border-cyan-100'
                          }`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span
                            className={`text-sm font-medium ${
                              theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                            }`}
                          >
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </section>

        {/* Advanced Security Features - image right */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Card
              className={`overflow-hidden border-0 shadow-2xl rounded-2xl md:rounded-3xl p-0 ${
                theme === 'dark' ? 'glass-dark-rev' : 'glass-rev'
              }`}
            >
              <div className="flex flex-col lg:flex-row">
                <CardContent className="flex-1 p-6 md:p-8 lg:p-10 order-2 lg:order-1">
                  <h2
                    className={`text-xl md:text-2xl font-bold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Advanced Security Features
                  </h2>
                  <p
                    className={`text-sm md:text-base mb-6 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Protect your vehicle with:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                    {securityFeatures.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-3 p-3 md:p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
                            theme === 'dark'
                              ? 'bg-white/5 hover:bg-white/10 border border-white/10'
                              : 'bg-blue-50/80 hover:bg-blue-100/80 border border-blue-100'
                          }`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span
                            className={`text-sm font-medium ${
                              theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                            }`}
                          >
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
                <div className="relative w-full lg:w-2/5 min-h-[240px] lg:min-h-[320px] flex-shrink-0 order-1 lg:order-2">
                  <img
                    src={IMAGES.security}
                    alt="Security features"
                    className="img-cover w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/50 to-transparent lg:from-black/40" />
                  <div className="absolute bottom-4 right-4 lg:bottom-6 lg:right-6 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center feature-icon-glow-rev">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Smart Fleet & Driving Insights - image left */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Card
              className={`overflow-hidden border-0 shadow-2xl rounded-2xl md:rounded-3xl p-0 ${
                theme === 'dark' ? 'glass-dark-rev' : 'glass-rev'
              }`}
            >
              <div className="flex flex-col lg:flex-row">
                <div className="relative w-full lg:w-2/5 min-h-[240px] lg:min-h-[320px] flex-shrink-0">
                  <img
                    src={IMAGES.insights}
                    alt="Fleet insights"
                    className="img-cover w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent lg:from-black/40" />
                  <div className="absolute bottom-4 left-4 lg:bottom-6 lg:left-6 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center feature-icon-glow-rev">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                </div>
                <CardContent className="flex-1 p-6 md:p-8 lg:p-10">
                  <h2
                    className={`text-xl md:text-2xl font-bold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Smart Fleet & Driving Insights
                  </h2>
                  <p
                    className={`text-sm md:text-base mb-6 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Optimize performance with:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                    {insightsFeatures.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-3 p-3 md:p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
                            theme === 'dark'
                              ? 'bg-white/5 hover:bg-white/10 border border-white/10'
                              : 'bg-cyan-50/80 hover:bg-cyan-100/80 border border-cyan-100'
                          }`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span
                            className={`text-sm font-medium ${
                              theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                            }`}
                          >
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </section>

        {/* Professional Installation - image right */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <Card
              className={`overflow-hidden border-0 shadow-2xl rounded-2xl md:rounded-3xl p-0 ${
                theme === 'dark' ? 'glass-dark-rev' : 'glass-rev'
              }`}
            >
              <div className="flex flex-col lg:flex-row">
                <CardContent className="flex-1 p-6 md:p-8 lg:p-10 order-2 lg:order-1">
                  <h2
                    className={`text-xl md:text-2xl font-bold mb-4 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Professional Installation
                  </h2>
                  <ul className="space-y-3 md:space-y-4">
                    {installationFeatures.map((text, i) => (
                      <li
                        key={i}
                        className={`flex items-center gap-3 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        <CheckCircle
                          className={`w-5 h-5 flex-shrink-0 ${
                            theme === 'dark' ? 'text-cyan-400' : 'text-cyan-600'
                          }`}
                        />
                        <span className="text-sm md:text-base">{text}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <div className="relative w-full lg:w-2/5 min-h-[240px] lg:min-h-[300px] flex-shrink-0 order-1 lg:order-2">
                  <img
                    src={IMAGES.installation}
                    alt="Professional installation"
                    className="img-cover w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/50 to-transparent lg:from-black/40" />
                  <div className="absolute bottom-4 right-4 lg:bottom-6 lg:right-6 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center feature-icon-glow-rev">
                    <Plug className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Why Choose REV */}
        <section className="relative z-10 py-12 lg:py-16 px-4 sm:px-6 lg:px-8 pb-24">
          <div className="max-w-6xl mx-auto">
            <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl mb-10 h-48 sm:h-56 md:h-64">
              <img
                src={IMAGES.whyChoose}
                alt="Why choose REV"
                className="img-cover w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1449965404239-e8e4e2d2cceb?w=800&auto=format&fit=crop&q=80';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex items-end">
                <div className="p-6 md:p-8 w-full">
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    Why Choose <span className="text-cyan-300">REV?</span>
                  </h2>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {whyChoose.map((text, i) => (
                <Card
                  key={i}
                  className={`border-0 shadow-xl rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
                    theme === 'dark' ? 'glass-dark-rev' : 'glass-rev'
                  }`}
                >
                  <CardContent className="p-5 md:p-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <span
                      className={`font-medium text-sm md:text-base ${
                        theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                      }`}
                    >
                      {text}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button
                onClick={() => navigate('/contact-us')}
                className={`h-12 px-8 text-base font-semibold shadow-lg transition-all duration-300 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                }`}
              >
                Get REV Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DeviceRev;
